import { type MouseEvent, memo, type ReactNode, type RefObject, useCallback } from 'react';

import { LayoutGridIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { WorkspaceAgentMentionContextsKitDropdown } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionContextsKitDropdown';
import {
  workspaceAgentMentionContextsEmpty,
  workspaceAgentMentionContextsSelector,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerPopoverChrome';
import { type WorkspaceAgentBrandKitOption } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { type WorkspaceAgentMentionDismissReason } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';

export type WorkspaceAgentMentionContextsBodyProps = {
  brandKits: readonly WorkspaceAgentBrandKitOption[];
  children: ReactNode;
  emptyMessage: string;
  hasFailed: boolean;
  hasRows: boolean;
  isKitMenuOpen: boolean;
  kitMenuTriggerRef: RefObject<HTMLButtonElement>;
  listRef: RefObject<HTMLDivElement>;
  onDismiss: (reason: WorkspaceAgentMentionDismissReason) => void;
  onKitMenuOpenChange: (open: boolean) => void;
  onKitMenuOpening: () => void;
  onListMouseLeave: () => void;
  onListMouseMove: () => void;
  onRefocusEditor: () => void;
  onRetry: () => void;
  onSelectBrandKit: (brandKitId: string) => void;
  selectedBrandKitId: string | null;
  showEmpty: boolean;
};

export const WorkspaceAgentMentionContextsBody = memo(
  ({
    brandKits,
    children,
    emptyMessage,
    hasFailed,
    hasRows,
    isKitMenuOpen,
    kitMenuTriggerRef,
    listRef,
    onDismiss,
    onKitMenuOpenChange,
    onKitMenuOpening,
    onListMouseLeave,
    onListMouseMove,
    onRefocusEditor,
    onRetry,
    onSelectBrandKit,
    selectedBrandKitId,
    showEmpty,
  }: WorkspaceAgentMentionContextsBodyProps) => {
    const emptyStyles = workspaceAgentMentionContextsEmpty();
    const rowListStyles = workspaceAgentMentionContextsSelector().rowList();

    const handleRetryMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    }, []);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: pointer move/leave only drive preview chrome; rows stay operable.
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid="WORKSPACE_AGENT_MENTION_LIST_SCROLLER"
        onMouseLeave={onListMouseLeave}
        onMouseMove={onListMouseMove}
        ref={listRef}
      >
        <WorkspaceAgentMentionContextsKitDropdown
          brandKits={brandKits}
          onDismiss={onDismiss}
          onMenuOpening={onKitMenuOpening}
          onOpenChange={onKitMenuOpenChange}
          onRefocusEditor={onRefocusEditor}
          onSelectBrandKit={onSelectBrandKit}
          open={isKitMenuOpen}
          selectedBrandKitId={selectedBrandKitId}
          triggerRef={kitMenuTriggerRef}
        />
        {!isKitMenuOpen && hasRows ? <div className={rowListStyles}>{children}</div> : null}
        {!isKitMenuOpen && showEmpty ? (
          <div className={emptyStyles.root()} data-testid="WORKSPACE_AGENT_MENTION_CONTEXTS_EMPTY_STATE">
            <LayoutGridIcon aria-hidden className="size-8 shrink-0" />
            <p className={emptyStyles.message()} data-testid="WORKSPACE_AGENT_MENTION_CONTEXTS_EMPTY_STATE_MESSAGE">
              {emptyMessage}
            </p>
            {hasFailed ? (
              <Button
                appearance="ghost"
                color="gray"
                data-testid="WORKSPACE_AGENT_MENTION_CONTEXTS_EMPTY_STATE_ACTION"
                onClick={onRetry}
                onMouseDown={handleRetryMouseDown}
                size="small"
                // Not a tab stop — focus stays in the editor; keyboard users retry via Enter on the popup's onKeyDown.
                tabIndex={-1}
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentMentionContextsBody.displayName = 'WorkspaceAgentMentionContextsBody';
